import { Button, DatePicker, Dropdown, Segmented, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEmployeeNames, withSelectedEmployeeOption } from "@/shared/hooks/useEmployeeNames";
import type { ReportFilterState } from "../../hooks/useReportFilters";
import { useDepartmentOptions, useEmployeeOptions, useZoneOptions } from "../../hooks/useReports";
import type { ReportColumn } from "../../types";
import { matchPreset, PERIOD_PRESETS, type PeriodPreset, presetRange } from "../../utils/period";
import { type FilterChip, FilterChips } from "./FilterChips";

const { RangePicker } = DatePicker;

type FiltrKaliti = "employeeId" | "departmentId" | "zoneId";

/** Segmentning «maxsus oraliq» qiymati — hech bir variantga mos kelmaydi. */
const MAXSUS = "__custom";

interface Props {
	filters: ReportFilterState;
	onChange: (next: ReportFilterState) => void;
	columns: ReportColumn[];
	/** `YYYY-MM-DD` — test seam; berilmasa brauzer sanasi. */
	today?: string;
}

/**
 * Hisobot sahifasining yagona filtr paneli: davr segmenti + oraliq +
 * chiplar + «Filtr qo'shish» + «Tozalash».
 *
 * ⚠️ **Xodim filtri HAR hisobotda** — to'qqizala hisobot uni qo'llaydi
 * (`tasks`/`leads` o'z so'rovida, qolganlari `employeeScopeIds`
 * orqali).
 *
 * ⚠️ **Hudud filtri esa darvozalangan** — faqat `zoneName` ustuni bor
 * hisobotlarda (`zone_exits`, `leads`): `filters.zoneId` ni aynan
 * o'sha ikkitasining so'rov quruvchisi o'qiydi. Hamma joyda
 * ko'rsatish «filtr ishlamayapti» sinfidagi jim nuqson bo'lardi.
 *
 * ⚠️ Faol segment URL dagi `preset` dan EMAS, sanalarning o'zidan
 * aniqlanadi (`matchPreset`) — kecha saqlangan «Bugun» havolasi bugun
 * yolg'on belgilanmasin.
 *
 * ⚠️ Sana `YYYY-MM-DD` — backend `sanaSchema` ISO-vaqtni rad etadi.
 * `allowClear={false}` — oraliq MAJBURIY, bo'sh holat 422 berardi.
 */
export function ReportFilterBar({ filters, onChange, columns, today }: Props) {
	const { t } = useTranslation("reports");
	const [qoshilayotgan, setQoshilayotgan] = useState<FiltrKaliti | null>(null);
	const [xodimQidiruv, setXodimQidiruv] = useState("");
	const hududKerak = columns.some((ustun) => ustun.key === "zoneName");
	const bugun = today ?? dayjs().format("YYYY-MM-DD");

	const { data: xodimlar = [] } = useEmployeeOptions(xodimQidiruv);
	const { data: bolimlar = [] } = useDepartmentOptions();
	const { data: hududlar = [] } = useZoneOptions(hududKerak);
	const xodimNomlari = useEmployeeNames([filters.employeeId]);

	const faolPreset = matchPreset(filters, bugun);
	const oraliq: [Dayjs, Dayjs] = [dayjs(filters.dateFrom), dayjs(filters.dateTo)];

	function ozgartir(next: ReportFilterState) {
		onChange(next);
		setQoshilayotgan(null);
	}

	function olibTashla(key: string) {
		// `undefined` — `serializeReportFilters` bo'sh qiymatni URL ga yozmaydi.
		onChange({ ...filters, [key]: undefined });
	}

	const nomsiz = t("filters.chips.unknown");
	const chiplar: FilterChip[] = [];

	if (filters.employeeId) {
		chiplar.push({
			key: "employeeId",
			label: t("filters.chips.employee", {
				name: xodimNomlari.get(filters.employeeId) ?? nomsiz,
			}),
		});
	}

	if (filters.departmentId) {
		chiplar.push({
			key: "departmentId",
			label: t("filters.chips.department", {
				name: bolimlar.find((b) => b.id === filters.departmentId)?.name ?? nomsiz,
			}),
		});
	}

	if (hududKerak && filters.zoneId) {
		chiplar.push({
			key: "zoneId",
			label: t("filters.chips.zone", {
				name: hududlar.find((h) => h.id === filters.zoneId)?.name ?? nomsiz,
			}),
		});
	}

	const qoshishVariantlari: { key: FiltrKaliti; label: string }[] = [
		{ key: "employeeId", label: t("filters.employeePlaceholder") },
		{ key: "departmentId", label: t("filters.departmentPlaceholder") },
		...(hududKerak ? [{ key: "zoneId" as const, label: t("filters.zonePlaceholder") }] : []),
	];

	const tanlovUmumiy = {
		allowClear: true,
		showSearch: true,
		autoFocus: true,
		defaultOpen: true,
		size: "small" as const,
		style: { minWidth: 220 },
		onOpenChange: (ochiq: boolean) => {
			if (!ochiq) {
				setQoshilayotgan(null);
			}
		},
	};

	return (
		<div data-testid="report-filter-bar" className="flex flex-col gap-2.5">
			<div className="flex flex-wrap items-center gap-2.5">
				<Segmented<string>
					aria-label={t("filters.periodLabel")}
					value={faolPreset ?? MAXSUS}
					options={PERIOD_PRESETS.map((preset) => ({
						value: preset,
						label: <span data-preset={preset}>{t(`filters.presets.${preset}`)}</span>,
					}))}
					onChange={(value) => {
						const preset = value as PeriodPreset;
						onChange({ ...filters, ...presetRange(preset, bugun), preset });
					}}
				/>

				<RangePicker
					allowClear={false}
					value={oraliq}
					onChange={(value) => {
						if (!(value?.[0] && value?.[1])) {
							return;
						}

						// ⚠️ Qo'lda tanlangan oraliq presetni BEKOR qiladi.
						const { preset: _eski, ...qolgan } = filters;
						onChange({
							...qolgan,
							dateFrom: value[0].format("YYYY-MM-DD"),
							dateTo: value[1].format("YYYY-MM-DD"),
						});
					}}
				/>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<FilterChips
					chips={chiplar}
					onRemove={olibTashla}
					removeLabel={(chip) => t("filters.removeChip", { label: chip.label })}
				/>

				{qoshilayotgan === "employeeId" ? (
					<Select
						{...tanlovUmumiy}
						filterOption={false}
						placeholder={t("filters.employeePlaceholder")}
						value={filters.employeeId}
						onSearch={setXodimQidiruv}
						options={withSelectedEmployeeOption(
							xodimlar.map((xodim) => ({ value: xodim.id, label: xodim.fullName })),
							filters.employeeId,
							xodimNomlari
						)}
						onChange={(employeeId?: string) => ozgartir({ ...filters, employeeId })}
					/>
				) : null}

				{qoshilayotgan === "departmentId" ? (
					<Select
						{...tanlovUmumiy}
						optionFilterProp="label"
						placeholder={t("filters.departmentPlaceholder")}
						value={filters.departmentId}
						options={bolimlar.map((bolim) => ({ value: bolim.id, label: bolim.name }))}
						onChange={(departmentId?: string) => ozgartir({ ...filters, departmentId })}
					/>
				) : null}

				{qoshilayotgan === "zoneId" && hududKerak ? (
					<Select
						{...tanlovUmumiy}
						optionFilterProp="label"
						placeholder={t("filters.zonePlaceholder")}
						value={filters.zoneId}
						options={hududlar.map((hudud) => ({ value: hudud.id, label: hudud.name }))}
						onChange={(zoneId?: string) => ozgartir({ ...filters, zoneId })}
					/>
				) : null}

				<Dropdown
					trigger={["click"]}
					menu={{
						items: qoshishVariantlari.map((v) => ({ key: v.key, label: v.label })),
						onClick: ({ key }) => setQoshilayotgan(key as FiltrKaliti),
					}}
				>
					<Button size="small" type="dashed" data-testid="add-filter">
						{`+ ${t("filters.addFilter")}`}
					</Button>
				</Dropdown>

				{chiplar.length > 0 ? (
					<Button
						size="small"
						type="link"
						data-testid="clear-filters"
						onClick={() =>
							// ⚠️ Sanalar va preset SAQLANADI — oraliq majburiy.
							ozgartir({
								dateFrom: filters.dateFrom,
								dateTo: filters.dateTo,
								...(filters.preset ? { preset: filters.preset } : {}),
							})
						}
					>
						{t("filters.clear")}
					</Button>
				) : null}
			</div>
		</div>
	);
}
